import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createRef, } from 'react';
import { BEHAVIOR_AUTO, BEHAVIOR_INSTANT, CLASS_LIST_HORIZONTAL, CLASS_LIST_VERTICAL, DEFAULT_BUFFER_SIZE, DEFAULT_DIRECTION, DEFAULT_DYNAMIC_SIZE, DEFAULT_ENABLED_BUFFER_OPTIMIZATION, DEFAULT_ITEM_SIZE, DEFAULT_LIST_SIZE, DEFAULT_MAX_BUFFER_SIZE, DEFAULT_SNAP, DEFAULT_SNAPPING_METHOD, HEIGHT_PROP_NAME, LEFT_PROP_NAME, MAX_SCROLL_TO_ITERATIONS, PX, SCROLL, SCROLL_END, TOP_PROP_NAME, TRACK_BY_PROPERTY_NAME, WIDTH_PROP_NAME, } from './const';
import { debounce, isDirection, ScrollEvent, TrackBox } from './utils';
import { Directions } from './enums';
import { VirtualListItem } from './components';
import { TRACK_BOX_CHANGE_EVENT_NAME } from './utils/trackBox';
import { FIREFOX_SCROLLBAR_OVERLAP_SIZE, IS_FIREFOX } from './utils/browser';
import { isSnappingMethodAdvenced } from './utils/snapping-method';
/**
 * Virtual list component.
 * Maximum performance for extremely large lists.
 * It is based on algorithms for virtualization of screen objects.
 * @link https://github.com/DjonnyX/rcx-virtual-list/tree/main/src/lib/src/ng-virtual-list.component.ts
 * @author Evgenii Grebennikov
 * @email djonnyx@gmail.com
 */
export class VirtualList extends React.Component {
    /**
     * Readonly. Returns the unique identifier of the component.
     */
    get id() { return this._id; }
    /**
     * Collection of list items.
     */
    set items(v) {
        if (this._items !== v) {
            this._items = this._itemsTransform(v);
        }
    }
    get items() {
        return this._items;
    }
    /**
     * Determines whether elements will snap. Default value is "true".
     */
    set snap(v) {
        if (this._snap !== v) {
            this._snap = v;
        }
    }
    get snap() {
        return this._snap;
    }
    /**
     * Experimental!
     * Enables buffer optimization.
     * Can only be used if items in the collection are not added or updated. Otherwise, artifacts in the form of twitching of the scroll area are possible.
     * Works only if the property dynamic = true
     */
    set enabledBufferOptimization(v) {
        if (this._enabledBufferOptimization !== v) {
            this._enabledBufferOptimization = v;
        }
    }
    get enabledBufferOptimization() {
        return this._enabledBufferOptimization;
    }
    /**
     * Rendering element template.
     */
    set itemRenderer(v) {
        if (this._itemRenderer !== v) {
            this._itemRenderer = v;
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { itemRenderer: { renderer: this._itemRenderer } })));
            }
        }
    }
    get itemRenderer() {
        return this._itemRenderer;
    }
    /**
     * Dictionary zIndex by id of the list element. If the value is not set or equal to 0,
     * then a simple element is displayed, if the value is greater than 0, then the sticky position mode is enabled for the element.
     */
    set stickyMap(v) {
        if (this._stickyMap !== v) {
            this._stickyMap = v;
        }
    }
    get stickyMap() {
        return this._stickyMap;
    }
    /**
     * If direction = 'vertical', then the height of a typical element. If direction = 'horizontal', then the width of a typical element.
     * Ignored if the dynamicSize property is true.
     */
    set itemSize(v) {
        if (this._itemSize !== v) {
            this._itemSize = this._itemSizeTransform(v);
        }
    }
    get itemSize() {
        return this._itemSize;
    }
    /**
     * If true then the items in the list can have different sizes and the itemSize property is ignored.
     * If false then the items in the list have a fixed size specified by the itemSize property. The default value is false.
     */
    set dynamicSize(v) {
        if (this._dynamicSize !== v) {
            this._dynamicSize = v;
            this.listenCacheChangesIfNeed(v);
        }
    }
    get dynamicSize() {
        return this._dynamicSize;
    }
    /**
     * Determines the direction in which elements are placed. Default value is "vertical".
     */
    set direction(v) {
        if (this._direction !== v) {
            this._direction = v;
            this._isVertical = this.getIsVertical(v);
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { isVertical: this._isVertical })));
            }
        }
    }
    get direction() {
        return this._direction;
    }
    /**
     * Number of elements outside the scope of visibility. Default value is 2.
     */
    set bufferSize(v) {
        if (this._bufferSize !== v) {
            this._bufferSize = v;
        }
    }
    get bufferSize() {
        return this._bufferSize;
    }
    /**
     * Number of elements outside the scope of visibility. Default value is 2.
     */
    set maxBufferSize(v) {
        const bufferSize = this.bufferSize;
        if (v === undefined || v <= bufferSize) {
            this._maxBufferSize = bufferSize;
            return;
        }
        if (this._maxBufferSize !== v) {
            this._maxBufferSize = v;
        }
    }
    get maxBufferSize() {
        return this._maxBufferSize;
    }
    /**
     * Snapping method.
     * 'default' - Normal group rendering.
     * 'advanced' - The group is rendered on a transparent background. List items below the group are not rendered.
     */
    set snappingMethod(v) {
        if (this._snappingMethod !== v) {
            this._snappingMethod = v;
            this._isSnappingMethodAdvanced = this.getIsSnappingMethodAdvanced(v);
            this._trackBox.isSnappingMethodAdvanced = this._isSnappingMethodAdvanced;
        }
    }
    get snappingMethod() {
        return this._snappingMethod;
    }
    /**
     * The name of the property by which tracking is performed
     */
    set trackBy(v) {
        if (this._trackBy !== v) {
            this._trackBy = v;
            this._trackBox.trackingPropertyName = v;
        }
    }
    get trackBy() {
        return this._trackBy;
    }
    set displayComponentsList(v) {
        if (this._displayComponentsList !== v) {
            this._displayComponentsList = v;
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { displayComponentsList: this._displayComponentsList })));
            }
        }
    }
    set bounds(v) {
        if (this._bounds !== v) {
            this._bounds = v;
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { bounds: this._bounds })));
            }
        }
    }
    get bounds() { return this._bounds; }
    set scrollSize(v) {
        if (this._scrollSize !== v) {
            this._scrollSize = v;
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { scrollSize: this._scrollSize })));
            }
        }
    }
    get scrollSize() { return this._scrollSize; }
    set cacheVersion(v) {
        if (this._cacheVersion !== v) {
            this._cacheVersion = v;
            if (this._initialized) {
                this.setState(v => (Object.assign(Object.assign({}, v), { cacheVersion: this._cacheVersion })));
            }
        }
    }
    get cacheVersion() { return this._cacheVersion; }
    constructor(props) {
        super(props);
        this._id = VirtualList.__nextId;
        this._$elementRef = createRef();
        this._$containerRef = createRef();
        this._$snappedRef = createRef();
        this._$listRef = createRef();
        this._itemsTransform = (v) => {
            this._trackBox.resetCollection(v, this.itemSize);
            return v;
        };
        this._snap = DEFAULT_SNAP;
        this._enabledBufferOptimization = DEFAULT_ENABLED_BUFFER_OPTIMIZATION;
        this._stickyMap = {};
        this._itemSizeTransform = (v) => {
            if (v === undefined) {
                return DEFAULT_ITEM_SIZE;
            }
            const val = Number(v);
            return Number.isNaN(val) || val <= 0 ? DEFAULT_ITEM_SIZE : val;
        };
        this._itemSize = DEFAULT_ITEM_SIZE;
        this._dynamicSize = DEFAULT_DYNAMIC_SIZE;
        this._direction = DEFAULT_DIRECTION;
        this._bufferSize = DEFAULT_BUFFER_SIZE;
        this._maxBufferSize = DEFAULT_MAX_BUFFER_SIZE;
        this._snappingMethod = DEFAULT_SNAPPING_METHOD;
        this._trackBy = TRACK_BY_PROPERTY_NAME;
        /**
         * Dictionary of element sizes by their id
         */
        this._trackBox = new TrackBox(this.trackBy);
        this._displayComponents = [];
        this._snapedDisplayComponent = createRef();
        this._displayComponentsList = [];
        this._bounds = null;
        this._scrollSize = 0;
        this._cacheVersion = -1;
        this._resizeObserver = null;
        this._isSnappingMethodAdvanced = this.getIsSnappingMethodAdvanced();
        this._isVertical = this.getIsVertical();
        this._initialized = false;
        this._onTrackBoxChangeHandler = (v) => {
            this.cacheVersion = v;
        };
        this._resizeSnappedComponentHandler = () => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const list = this._$listRef, container = this._$containerRef, snappedComponent = (_a = this._snapedDisplayComponent) === null || _a === void 0 ? void 0 : _a.current;
            if (list && list.current && container && container.current && snappedComponent) {
                const isVertical = this._isVertical, listBounds = list.current.getBoundingClientRect(), listElement = list === null || list === void 0 ? void 0 : list.current, { width: lWidth, height: lHeight } = (_b = listElement === null || listElement === void 0 ? void 0 : listElement.getBoundingClientRect()) !== null && _b !== void 0 ? _b : { width: 0, height: 0 }, { width, height } = (_d = (_c = container.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect()) !== null && _d !== void 0 ? _d : { width: 0, height: 0 }, isScrollable = isVertical ? container.current.scrollHeight > 0 : container.current.scrollWidth > 0;
                let scrollBarSize = isVertical ? width - lWidth : height - lHeight, isScrollBarOverlap = true, overlapScrollBarSize = 0;
                if (scrollBarSize === 0 && isScrollable) {
                    isScrollBarOverlap = true;
                }
                if (isScrollBarOverlap && IS_FIREFOX) {
                    scrollBarSize = overlapScrollBarSize = FIREFOX_SCROLLBAR_OVERLAP_SIZE;
                }
                const element = snappedComponent.element;
                if (element) {
                    element.style.clipPath =
                        `path("M 0 0 L 0 ${element.offsetHeight} L ${element.offsetWidth - overlapScrollBarSize} ${element.offsetHeight} L ${element.offsetWidth - overlapScrollBarSize} 0 Z")`;
                    snappedComponent.regularLength = `${isVertical ? listBounds.width : listBounds.height}${PX}`;
                    const { width: sWidth, height: sHeight } = (_e = snappedComponent.getBounds()) !== null && _e !== void 0 ? _e : { width: 0, height: 0 }, containerElement = container.current;
                    if (containerElement) {
                        let left, right, top, bottom;
                        if (isVertical) {
                            const delta = (_g = (_f = snappedComponent.data) === null || _f === void 0 ? void 0 : _f.measures.delta) !== null && _g !== void 0 ? _g : 0;
                            left = 0;
                            right = width - scrollBarSize;
                            top = sHeight;
                            bottom = height;
                            containerElement.style.clipPath =
                                `path("M 0 ${top + delta} L 0 ${height} L ${width} ${height} L ${width} 0 L ${right} 0 L ${right} ${top + delta} Z")`;
                        }
                        else {
                            const delta = (_j = (_h = snappedComponent.data) === null || _h === void 0 ? void 0 : _h.measures.delta) !== null && _j !== void 0 ? _j : 0;
                            left = sWidth;
                            right = width;
                            top = 0;
                            bottom = height - scrollBarSize;
                            containerElement.style.clipPath =
                                `path("M ${left + delta} 0 L ${left + delta} ${bottom} L 0 ${bottom} L 0 ${height} L ${width} ${height} L ${width} 0 Z")`;
                        }
                    }
                }
            }
        };
        this._observeComponentRenderersTimeout = undefined;
        this._scrollToRepeatExecutionTimeout = undefined;
        this._resizeSnappedObserver = null;
        this._componentsResizeObserver = new ResizeObserver(() => {
            this._trackBox.changes();
        });
        this._onResizeHandler = () => {
            var _a;
            const bounds = (_a = this._$containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (bounds) {
                this.bounds = ({ width: bounds.width, height: bounds.height });
            }
            else {
                this.bounds = ({ width: DEFAULT_LIST_SIZE, height: DEFAULT_LIST_SIZE });
            }
            if (this._isSnappingMethodAdvanced) {
                this._resizeSnappedComponentHandler();
            }
        };
        this._onScrollHandler = (e) => {
            var _a;
            this.disposeScrollToRepeatExecutionTimeout();
            const container = (_a = this._$containerRef) === null || _a === void 0 ? void 0 : _a.current;
            if (container) {
                const scrollSize = (this._isVertical ? container.scrollTop : container.scrollLeft), actualScrollSize = scrollSize;
                this.scrollSize = actualScrollSize;
            }
        };
        this._onScrollEndHandler = (e) => {
            var _a;
            this.disposeScrollToRepeatExecutionTimeout();
            const container = (_a = this._$containerRef) === null || _a === void 0 ? void 0 : _a.current;
            if (container) {
                container.removeEventListener(SCROLL_END, this._onScrollEndHandler);
            }
        };
        this._onContainerScrollHandler = (e) => {
            var _a, _b;
            const container = (_a = this._$containerRef) === null || _a === void 0 ? void 0 : _a.current, list = (_b = this._$listRef) === null || _b === void 0 ? void 0 : _b.current;
            if (container && list) {
                const scrollSize = (this._isVertical ? container.scrollTop : container.scrollLeft);
                this._trackBox.deltaDirection = this._scrollSize > scrollSize ? -1 : this._scrollSize < scrollSize ? 1 : 0;
                const event = new ScrollEvent({
                    direction: this._trackBox.scrollDirection, container: container,
                    list, delta: this._trackBox.delta,
                    scrollDelta: this._trackBox.scrollDelta, isVertical: this._isVertical,
                });
                if (this._onScroll !== undefined) {
                    this._onScroll(event);
                }
            }
        };
        this._onContainerScrollEndHandler = (e) => {
            var _a, _b;
            const container = (_a = this._$containerRef) === null || _a === void 0 ? void 0 : _a.current, list = (_b = this._$listRef) === null || _b === void 0 ? void 0 : _b.current;
            if (container && list) {
                const scrollSize = (this._isVertical ? container.scrollTop : container.scrollLeft);
                this._trackBox.deltaDirection = this._scrollSize > scrollSize ? -1 : 0;
                const event = new ScrollEvent({
                    direction: this._trackBox.scrollDirection, container: container,
                    list, delta: this._trackBox.delta,
                    scrollDelta: this._trackBox.scrollDelta, isVertical: this._isVertical,
                });
                if (this._onScrollEnd !== undefined) {
                    this._onScrollEnd(event);
                }
            }
        };
        this._t = Date.now();
        this._debouncedInitialize = debounce(() => {
            if (!this._initialized) {
                this._trackBox.displayComponents = this._displayComponents;
                this.initializeComponent();
                this.initializeSnappedComponent();
                this.resetRenderers();
                this.observeComponentRenderers();
            }
            this._initialized = true;
        });
        this._resizeObserveQueue = [];
        this.resetBoundsSize = (isVertical, totalSize) => {
            const l = this._$listRef;
            if (l && l.current) {
                l.current.style[isVertical ? HEIGHT_PROP_NAME : WIDTH_PROP_NAME] = `${totalSize}${PX}`;
            }
        };
        const { direction = DEFAULT_DIRECTION, dynamicSize = DEFAULT_DYNAMIC_SIZE, enabledBufferOptimization = DEFAULT_ENABLED_BUFFER_OPTIMIZATION, bufferSize = DEFAULT_BUFFER_SIZE, maxBufferSize = DEFAULT_MAX_BUFFER_SIZE, itemRenderer, items, itemSize = DEFAULT_ITEM_SIZE, snap = DEFAULT_SNAP, snappingMethod = DEFAULT_SNAPPING_METHOD, stickyMap = {}, trackBy = TRACK_BY_PROPERTY_NAME, className, onScroll, onScrollEnd, } = props;
        this.direction = direction;
        this.dynamicSize = dynamicSize;
        this.enabledBufferOptimization = enabledBufferOptimization;
        this.bufferSize = bufferSize;
        this.maxBufferSize = maxBufferSize;
        this.items = items;
        this.itemRenderer = itemRenderer;
        this.itemSize = itemSize;
        this.snap = snap;
        this.snappingMethod = snappingMethod;
        this.stickyMap = stickyMap;
        this.trackBy = trackBy;
        this._className = className;
        this._onScroll = onScroll;
        this._onScrollEnd = onScrollEnd;
        this.state = {
            cacheVersion: this._cacheVersion,
            displayComponentsList: this._displayComponentsList,
            isVertical: this._isVertical,
            scrollSize: this._scrollSize,
            bounds: this._bounds,
        };
    }
    getSnapshotBeforeUpdate(prevProps, prevState) {
        return null;
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        // etc
    }
    shouldComponentUpdate(nextProps, nextState, nextContext) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        let needToUpdate = false;
        if (nextProps.direction !== this._direction) {
            this.direction = (_a = nextProps.direction) !== null && _a !== void 0 ? _a : DEFAULT_DIRECTION;
            needToUpdate = true;
        }
        if (nextProps.dynamicSize !== this._dynamicSize) {
            this.dynamicSize = (_b = nextProps.dynamicSize) !== null && _b !== void 0 ? _b : DEFAULT_DYNAMIC_SIZE;
            needToUpdate = true;
        }
        if (nextProps.enabledBufferOptimization !== this._enabledBufferOptimization) {
            this.enabledBufferOptimization = (_c = nextProps.enabledBufferOptimization) !== null && _c !== void 0 ? _c : DEFAULT_ENABLED_BUFFER_OPTIMIZATION;
            needToUpdate = true;
        }
        if (nextProps.itemRenderer !== this._itemRenderer) {
            this.itemRenderer = nextProps.itemRenderer;
            needToUpdate = true;
        }
        if (nextProps.itemSize !== this._itemSize) {
            this.itemSize = (_d = nextProps.itemSize) !== null && _d !== void 0 ? _d : DEFAULT_ITEM_SIZE;
            needToUpdate = true;
        }
        if (nextProps.items !== this._items) {
            this.items = (_e = nextProps.items) !== null && _e !== void 0 ? _e : [];
            needToUpdate = true;
        }
        if (nextProps.bufferSize !== this._bufferSize) {
            this.bufferSize = (_f = nextProps.bufferSize) !== null && _f !== void 0 ? _f : DEFAULT_BUFFER_SIZE;
            needToUpdate = true;
        }
        if (nextProps.maxBufferSize !== this._maxBufferSize) {
            this.maxBufferSize = (_g = nextProps.maxBufferSize) !== null && _g !== void 0 ? _g : DEFAULT_MAX_BUFFER_SIZE;
            needToUpdate = true;
        }
        if (nextProps.snap !== this._snap) {
            this.snap = (_h = nextProps.snap) !== null && _h !== void 0 ? _h : DEFAULT_SNAP;
            needToUpdate = true;
        }
        if (nextProps.snappingMethod !== this._snappingMethod) {
            this.snappingMethod = (_j = nextProps.snappingMethod) !== null && _j !== void 0 ? _j : DEFAULT_SNAPPING_METHOD;
            needToUpdate = true;
        }
        if (nextProps.stickyMap !== this._stickyMap) {
            this.stickyMap = (_k = nextProps.stickyMap) !== null && _k !== void 0 ? _k : {};
            needToUpdate = true;
        }
        if (nextProps.trackBy !== this._trackBy) {
            this.trackBy = (_l = nextProps.trackBy) !== null && _l !== void 0 ? _l : TRACK_BY_PROPERTY_NAME;
            needToUpdate = true;
        }
        if (nextProps.onScroll !== this._onScroll) {
            this._onScroll = nextProps.onScroll;
        }
        if (nextProps.onScrollEnd !== this._onScrollEnd) {
            this._onScrollEnd = nextProps.onScrollEnd;
        }
        if (this.state.displayComponentsList !== nextState.displayComponentsList
            || this.state.isVertical !== nextState.isVertical
            || this.state.scrollSize !== nextState.scrollSize
            || this.state.bounds !== nextState.bounds) {
            needToUpdate = true;
        }
        this.update();
        return needToUpdate;
    }
    componentDidMount() {
        this._debouncedInitialize.execute();
    }
    observeComponentRenderers() {
        this.disposeObserveComponentRenderers();
        this._observeComponentRenderersTimeout = setTimeout(() => {
            this.executeResizeObserverQueue();
            this.observeComponentRenderers();
        });
    }
    initializeComponent() {
        const containerEl = this._$containerRef.current;
        if (containerEl) {
            // for direction calculation
            containerEl.addEventListener(SCROLL, this._onContainerScrollHandler);
            containerEl.addEventListener(SCROLL_END, this._onContainerScrollEndHandler);
            containerEl.addEventListener(SCROLL, this._onScrollHandler);
            this._resizeObserver = new ResizeObserver(this._onResizeHandler);
            this._resizeObserver.observe(containerEl);
            this._onResizeHandler();
        }
    }
    update() {
        var _a, _b, _c, _d, _e, _f, _g;
        const items = this._items, bounds = this._bounds, initialized = this._initialized;
        if (initialized && bounds && items) {
            const { width, height } = bounds, isVertical = this._isVertical, stickyMap = this._stickyMap, itemSize = this._itemSize, dynamicSize = this._dynamicSize, snap = this._snap, bufferSize = this._bufferSize, maxBufferSize = this._maxBufferSize, enabledBufferOptimization = this._enabledBufferOptimization, scrollSize = (_f = (this._isVertical
                ? (_c = (_b = (_a = this._$containerRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.scrollTop) !== null && _c !== void 0 ? _c : 0
                : (_e = (_d = this._$containerRef) === null || _d === void 0 ? void 0 : _d.current) === null || _e === void 0 ? void 0 : _e.scrollLeft)) !== null && _f !== void 0 ? _f : 0;
            let actualScrollSize = scrollSize;
            const opts = {
                bounds: { width, height }, dynamicSize, isVertical, itemSize,
                bufferSize, maxBufferSize, scrollSize: actualScrollSize, snap, enabledBufferOptimization,
            };
            const { displayItems, totalSize } = this._trackBox.updateCollection(items, stickyMap, opts);
            this.resetBoundsSize(isVertical, totalSize);
            this.createDisplayComponentsIfNeed(displayItems);
            this.tracking();
            if (this._isSnappingMethodAdvanced) {
                this._resizeSnappedComponentHandler();
            }
            const container = this._$containerRef;
            if (container) {
                const delta = this._trackBox.delta;
                actualScrollSize = scrollSize + delta;
                this._trackBox.clearDelta();
                if (this._scrollSize !== actualScrollSize) {
                    const params = {
                        [this._isVertical ? TOP_PROP_NAME : LEFT_PROP_NAME]: actualScrollSize,
                        behavior: BEHAVIOR_INSTANT
                    };
                    (_g = container.current) === null || _g === void 0 ? void 0 : _g.scrollTo(params);
                    this.scrollSize = actualScrollSize;
                }
            }
        }
    }
    initializeSnappedComponent() {
        this._trackBox.snapedDisplayComponent = this._snapedDisplayComponent;
    }
    listenCacheChangesIfNeed(value) {
        if (value) {
            if (!this._trackBox.hasEventListener(TRACK_BOX_CHANGE_EVENT_NAME, this._onTrackBoxChangeHandler)) {
                this._trackBox.addEventListener(TRACK_BOX_CHANGE_EVENT_NAME, this._onTrackBoxChangeHandler);
            }
        }
        else {
            if (this._trackBox.hasEventListener(TRACK_BOX_CHANGE_EVENT_NAME, this._onTrackBoxChangeHandler)) {
                this._trackBox.removeEventListener(TRACK_BOX_CHANGE_EVENT_NAME, this._onTrackBoxChangeHandler);
            }
        }
    }
    getIsSnappingMethodAdvanced(m) {
        const method = m || this._snappingMethod;
        return isSnappingMethodAdvenced(method);
    }
    getIsVertical(d) {
        const dir = d || this._direction;
        return isDirection(dir, Directions.VERTICAL);
    }
    /**
     * Returns the bounds of an element with a given id
     */
    getItemBounds(id) {
        return this._trackBox.getItemBounds(id);
    }
    scrollToExecutor(id, behavior, iteration = 0, isLastIteration = false) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        const items = this._items, bounds = this._bounds, dynamicSize = this._dynamicSize, isVertical = this._isVertical, itemSize = this._itemSize, bufferSize = this._bufferSize, maxBufferSize = this._maxBufferSize, snap = this._snap, trackBox = this._trackBox, enabledBufferOptimization = this._enabledBufferOptimization, stickyMap = this._stickyMap;
        if (!bounds || !items || !items.length) {
            return;
        }
        const container = this._$containerRef;
        if (container) {
            this.disposeScrollToRepeatExecutionTimeout();
            if (dynamicSize) {
                (_a = container.current) === null || _a === void 0 ? void 0 : _a.removeEventListener(SCROLL_END, this._onScrollEndHandler);
                if (container && container.current) {
                    container.current.removeEventListener(SCROLL, this._onScrollHandler);
                }
                const { width, height } = bounds, delta = trackBox.delta, opts = {
                    bounds: { width, height }, collection: items, dynamicSize, isVertical, itemSize,
                    bufferSize, maxBufferSize, scrollSize: (isVertical ? (_c = (_b = container.current) === null || _b === void 0 ? void 0 : _b.scrollTop) !== null && _c !== void 0 ? _c : 0
                        : (_e = (_d = container.current) === null || _d === void 0 ? void 0 : _d.scrollLeft) !== null && _e !== void 0 ? _e : 0) + delta,
                    snap, fromItemId: id, enabledBufferOptimization,
                }, scrollSize = trackBox.getItemPosition(id, stickyMap, opts), params = { [isVertical ? TOP_PROP_NAME : LEFT_PROP_NAME]: scrollSize, behavior };
                if (scrollSize === -1) {
                    (_f = container.current) === null || _f === void 0 ? void 0 : _f.addEventListener(SCROLL_END, this._onScrollEndHandler);
                    (_g = container.current) === null || _g === void 0 ? void 0 : _g.addEventListener(SCROLL, this._onScrollHandler);
                    return;
                }
                trackBox.clearDelta();
                if (container) {
                    const { displayItems, totalSize } = trackBox.updateCollection(items, stickyMap, Object.assign(Object.assign({}, opts), { scrollSize, fromItemId: isLastIteration ? undefined : id })), delta = trackBox.delta;
                    trackBox.clearDelta();
                    let actualScrollSize = scrollSize + delta;
                    this.resetBoundsSize(isVertical, totalSize);
                    this.createDisplayComponentsIfNeed(displayItems);
                    this.tracking();
                    const _scrollSize = trackBox.getItemPosition(id, stickyMap, Object.assign(Object.assign({}, opts), { scrollSize: actualScrollSize, fromItemId: id })), notChanged = actualScrollSize === _scrollSize;
                    if (_scrollSize === -1) {
                        (_h = container.current) === null || _h === void 0 ? void 0 : _h.addEventListener(SCROLL_END, this._onScrollEndHandler);
                        (_j = container.current) === null || _j === void 0 ? void 0 : _j.addEventListener(SCROLL, this._onScrollHandler);
                        return;
                    }
                    if (!notChanged || iteration < MAX_SCROLL_TO_ITERATIONS) {
                        this.disposeScrollToRepeatExecutionTimeout();
                        this._scrollToRepeatExecutionTimeout = setTimeout(() => {
                            this.scrollToExecutor(id, BEHAVIOR_INSTANT, iteration + 1, notChanged);
                        });
                    }
                    else {
                        this.scrollSize = actualScrollSize;
                        (_k = container.current) === null || _k === void 0 ? void 0 : _k.addEventListener(SCROLL, this._onScrollHandler);
                    }
                }
                (_l = container.current) === null || _l === void 0 ? void 0 : _l.scrollTo(params);
                this.scrollSize = scrollSize;
            }
            else {
                const _scrollSize = (isVertical ? (_o = (_m = container.current) === null || _m === void 0 ? void 0 : _m.scrollTop) !== null && _o !== void 0 ? _o : 0 : (_q = (_p = container.current) === null || _p === void 0 ? void 0 : _p.scrollLeft) !== null && _q !== void 0 ? _q : 0), index = items.findIndex(item => item.id === id);
                if (index > -1) {
                    const scrollSize = index * itemSize;
                    if (_scrollSize !== scrollSize) {
                        const params = { [isVertical ? TOP_PROP_NAME : LEFT_PROP_NAME]: scrollSize, behavior };
                        (_r = container.current) === null || _r === void 0 ? void 0 : _r.scrollTo(params);
                    }
                }
            }
        }
    }
    resetRenderers() {
        const doMap = {}, components = this._displayComponents;
        for (let i = 0, l = components.length; i < l; i++) {
            const item = components[i].current;
            if (item) {
                const id = item.id;
                doMap[id] = i;
            }
        }
        this._trackBox.setDisplayObjectIndexMapById(doMap);
    }
    executeResizeObserverQueue() {
        var _a, _b;
        let isChanged = false;
        const queue = this._resizeObserveQueue, newQueue = [];
        for (let l = queue.length, ei = l - 1, i = ei; i >= 0; i--) {
            const ref = queue[i], el = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.element;
            if (el) {
                isChanged = true;
                (_b = this._componentsResizeObserver) === null || _b === void 0 ? void 0 : _b.observe(el);
                this._resizeObserveQueue.slice(i, 1);
                continue;
            }
            newQueue.push(ref);
        }
        if (isChanged) {
            this.displayComponentsList = [...this._displayComponentsList];
        }
        const snappedDisplayComponentRef = this._snapedDisplayComponent.current;
        this._trackBox.snapedDisplayComponent = this._snapedDisplayComponent;
        if (snappedDisplayComponentRef && !this._resizeSnappedObserver) {
            const element = snappedDisplayComponentRef === null || snappedDisplayComponentRef === void 0 ? void 0 : snappedDisplayComponentRef.element;
            if (element) {
                this._resizeSnappedObserver = new ResizeObserver(this._resizeSnappedComponentHandler);
                this._resizeSnappedObserver.observe(element);
            }
        }
        this._resizeObserveQueue = newQueue;
    }
    waitToResizeObserve(ref) {
        this._resizeObserveQueue.push(ref);
    }
    createDisplayComponentsIfNeed(displayItems) {
        if (!displayItems || !this._$listRef) {
            this._trackBox.setDisplayObjectIndexMapById({});
            return;
        }
        this._trackBox.items = displayItems;
        let isChanged = false;
        const _listContainerRef = this._$listRef, components = this._displayComponents;
        while (components.length < displayItems.length) {
            if (_listContainerRef) {
                isChanged = true;
                const ref = createRef();
                components.push(ref);
                this.waitToResizeObserve(ref);
            }
        }
        if (isChanged) {
            this.displayComponentsList = [...components];
        }
        this.resetRenderers();
    }
    /**
     * Tracking by id
     */
    tracking() {
        this._trackBox.track();
    }
    /**
     * The method scrolls the list to the element with the given id and returns the value of the scrolled area.
     * Behavior accepts the values ​​"auto", "instant" and "smooth".
     */
    scrollTo(id, behavior = BEHAVIOR_AUTO) {
        this.scrollToExecutor(id, behavior);
    }
    /**
     * Scrolls the scroll area to the desired element with the specified ID.
     */
    scrollToEnd(behavior = BEHAVIOR_INSTANT) {
        const items = this._items;
        const latItem = items === null || items === void 0 ? void 0 : items[items.length > 0 ? items.length - 1 : 0];
        if (latItem) {
            this.scrollTo(latItem.id, behavior);
        }
    }
    render() {
        const { displayComponentsList } = this.state;
        const isVertical = this._isVertical;
        return _jsxs("div", Object.assign({ ref: this._$elementRef, className: `rcxvl ${this._className} ${isVertical ? CLASS_LIST_VERTICAL : CLASS_LIST_HORIZONTAL}` }, { children: [this._snap && this._isSnappingMethodAdvanced &&
                    _jsx("div", Object.assign({ ref: this._$snappedRef, className: "rcxvl__list-snapper snapped-item" }, { children: _jsx(VirtualListItem, { ref: this._snapedDisplayComponent, regular: true, renderer: this._itemRenderer }) })), _jsx("div", Object.assign({ ref: this._$containerRef, className: "rcxvl__scroller" }, { children: _jsx("ul", Object.assign({ ref: this._$listRef, className: "rcxvl__list" }, { children: displayComponentsList.map((ref, index) => _jsx(VirtualListItem, { ref: ref, renderer: this._itemRenderer }, String(index))) })) }))] }));
    }
    disposeScrollToRepeatExecutionTimeout() {
        clearTimeout(this._scrollToRepeatExecutionTimeout);
    }
    disposeObserveComponentRenderers() {
        clearTimeout(this._observeComponentRenderersTimeout);
    }
    componentWillUnmount() {
        this.disposeScrollToRepeatExecutionTimeout();
        this.disposeObserveComponentRenderers();
        if (this._debouncedInitialize) {
            this._debouncedInitialize.dispose();
        }
        if (this._trackBox) {
            this._trackBox.dispose();
        }
        if (this._componentsResizeObserver) {
            this._componentsResizeObserver.disconnect();
        }
        if (this._resizeSnappedObserver) {
            this._resizeSnappedObserver.disconnect();
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        const containerEl = this._$containerRef.current;
        if (containerEl) {
            containerEl.removeEventListener(SCROLL, this._onScrollHandler);
            containerEl.removeEventListener(SCROLL, this._onContainerScrollHandler);
            containerEl.removeEventListener(SCROLL_END, this._onContainerScrollEndHandler);
        }
    }
}
VirtualList.__nextId = 0;
